/**
 * URL-backed query state + data loading for the custom stat field registry.
 *
 * The whole query (search, unit filter, data-type filter, "show retired", sort,
 * page, page size) lives in the address bar, so a refresh, a bookmark and a
 * shared link all restore the same view. Same idiom as `use-team-users.ts`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { ApiError } from '../../lib/api-error';
import {
  STAT_FIELD_APPLIES_TO,
  STAT_FIELD_DATA_TYPES,
  STAT_FIELD_SORT_FIELDS,
  type StatFieldAppliesTo,
  type StatFieldCapacity,
  type StatFieldDataType,
  type StatFieldDefinition,
  type StatFieldEnumOption,
  type StatFieldSortField,
  type StatSchemaPage,
} from '../../lib/api-types';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../../lib/constants';
import {
  boolParam,
  enumParam,
  intChoiceParam,
  intParam,
  readUrlState,
  textParam,
  writeUrlState,
  URL_PARAM_KEYS,
} from '../../lib/url-state';

export interface StatFieldsQueryState {
  search: string;
  unit: StatFieldAppliesTo | 'all';
  dataType: StatFieldDataType | 'all';
  sortBy: StatFieldSortField;
  sortOrder: 'asc' | 'desc';
  page: number;
  pageSize: number;
  /** Include retired (inactive) fields, so an admin can review or restore them. */
  isShowingRetired: boolean;
  /** Include platform built-ins alongside the team's own fields. */
  isShowingBuiltin: boolean;
}

/** Declarative screen state (shared url-state kit): parse is total, defaults stay off the URL. */
const STAT_FIELDS_URL_STATE = {
  search: textParam(URL_PARAM_KEYS.search),
  unit: enumParam(URL_PARAM_KEYS.unit, [...STAT_FIELD_APPLIES_TO, 'all'] as const, 'all'),
  dataType: enumParam('type', [...STAT_FIELD_DATA_TYPES, 'all'] as const, 'all'),
  sortBy: enumParam(URL_PARAM_KEYS.sortBy, STAT_FIELD_SORT_FIELDS, 'display_order'),
  sortOrder: enumParam(URL_PARAM_KEYS.sortOrder, ['asc', 'desc'] as const, 'asc'),
  page: intParam(URL_PARAM_KEYS.page, { fallback: 1, min: 1 }),
  pageSize: intChoiceParam(URL_PARAM_KEYS.pageSize, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE),
  isShowingRetired: boolParam('retired'),
  isShowingBuiltin: boolParam('builtin'),
};

function readQuery(params: URLSearchParams): StatFieldsQueryState {
  return readUrlState(STAT_FIELDS_URL_STATE, params);
}

export interface StatFieldsController {
  query: StatFieldsQueryState;
  setQuery: (patch: Partial<StatFieldsQueryState>) => void;
  fields: readonly StatFieldDefinition[];
  total: number;
  capacity: StatFieldCapacity | null;
  enumCategories: readonly { category: string; values: StatFieldEnumOption[] }[];
  canWrite: boolean;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

export function useStatFields(): StatFieldsController {
  const apiClient = useApiClient();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState<StatSchemaPage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const query = useMemo(() => readQuery(params), [params]);

  const setQuery = useCallback(
    (patch: Partial<StatFieldsQueryState>) => {
      const next = { ...readQuery(params), ...patch };
      if (patch.page === undefined) next.page = 1;
      setParams(writeUrlState(STAT_FIELDS_URL_STATE, next, params), { replace: true });
    },
    [params, setParams]
  );

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    apiClient
      .listStatFields(
        {
          q: query.search === '' ? undefined : query.search,
          unit: query.unit === 'all' ? undefined : query.unit,
          include_builtin: query.isShowingBuiltin,
          include_disabled: query.isShowingRetired ? true : undefined,
          sort_by: query.sortBy,
          sort_order: query.sortOrder,
          page: query.page,
          limit: query.pageSize,
        },
        controller.signal
      )
      .then((result) => {
        if (controller.signal.aborted) return;
        setPage(result);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          ApiError.isApiError(caught)
            ? caught.message
            : 'The custom stat fields could not be loaded. Please try again.'
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [apiClient, query, reloadToken]);

  // `data_type` is a client-side narrowing: the service paginates on the other
  // dimensions and the catalog is small enough (<= a few dozen) that filtering
  // the page in the browser cannot hide a row the user asked for.
  const fields = useMemo(() => {
    const items = page?.items ?? [];
    return query.dataType === 'all'
      ? items
      : items.filter((field) => field.data_type === query.dataType);
  }, [page, query.dataType]);

  return {
    query,
    setQuery,
    fields,
    total: page?.total ?? 0,
    capacity: page?.capacity ?? null,
    enumCategories: page?.enum_categories ?? [],
    canWrite: page?.can_write ?? false,
    isLoading,
    error,
    reload,
  };
}
